import { useState, useEffect, useCallback, useMemo, useRef, useImperativeHandle } from 'react'

import { defaultFilterOption } from './types'

import type { SelectOption, SearchableSelectValidationState } from '../searchable-select'
import type { MultiSelectProps } from './types'

// ============================================================================
// Dropdown position hook
// ============================================================================

interface UseDropdownPositionOptions {
  containerRef: React.RefObject<HTMLDivElement>
  filteredOptionsCount: number
  isModal: boolean
  isMobile: boolean
  isOpen: boolean
  maxVisibleItems: number
}

export function useDropdownPosition(opts: UseDropdownPositionOptions): { top: number; left: number; width: number } | null {
  const { containerRef, filteredOptionsCount, isModal, isMobile, isOpen, maxVisibleItems } = opts
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null)

  useEffect(() => {
    if (!isOpen || isMobile || isModal) { return }

    const updatePosition = (): void => {
      if (!containerRef.current) { return }
      const rect = containerRef.current.getBoundingClientRect()
      const estimatedHeight = Math.min(filteredOptionsCount * 38 + 120, maxVisibleItems * 38 + 120)
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top
      /* v8 ignore next */
      const shouldPositionAbove = spaceBelow < estimatedHeight && spaceAbove > spaceBelow
      /* v8 ignore next 5 */
      if (shouldPositionAbove) {
        setPosition({ top: rect.top + window.scrollY - estimatedHeight - 4, left: rect.left + window.scrollX, width: rect.width })
      } else {
        setPosition({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX, width: rect.width })
      }
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen, isMobile, isModal, filteredOptionsCount, maxVisibleItems, containerRef])

  return position
}

// ============================================================================
// Body scroll lock
// ============================================================================

export function useBodyScrollLock(isOpen: boolean, isMobile: boolean): void {
  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [isOpen, isMobile])
}

// ============================================================================
// Outside click handler
// ============================================================================

export function useOutsideClick(
  isOpen: boolean,
  isMobile: boolean,
  containerRef: React.RefObject<HTMLDivElement>,
  dropdownRef: React.RefObject<HTMLDivElement>,
  onClose: () => void
): void {
  useEffect(() => {
    if (isMobile) { return }
    /* v8 ignore next 12 */
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        onClose()
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, isMobile, containerRef, dropdownRef, onClose])
}

// ============================================================================
// Derived state hook
// ============================================================================

interface MultiSelectDerivedArgs<T> {
  error: string | undefined
  filterOption: NonNullable<MultiSelectProps<T>['filterOption']>
  options: MultiSelectProps<T>['options']
  searchQuery: string
  validationStateProp: SearchableSelectValidationState | undefined
  value: T[]
}

interface MultiSelectDerived<T> {
  filteredOptions: SelectOption<T>[]
  selectedOptions: SelectOption<T>[]
  showError: boolean | '' | undefined
  validationState: SearchableSelectValidationState
}

export function useMultiSelectDerived<T>({
  error, filterOption, options, searchQuery, validationStateProp, value,
}: MultiSelectDerivedArgs<T>): MultiSelectDerived<T> {
  const selectedOptions = useMemo(() => options.filter((opt) => value.includes(opt.value)), [options, value])
  const filteredOptions = useMemo(() => {
    if (!searchQuery) { return options }
    return options.filter((opt) => filterOption(opt, searchQuery))
  }, [options, searchQuery, filterOption])
  const validationState = error ? 'invalid' : (validationStateProp ?? 'default')
  const showError = error && validationState === 'invalid'
  return { selectedOptions, filteredOptions, validationState, showError }
}

// ============================================================================
// Toggle option helper
// ============================================================================

export function computeToggleResult<T>(
  option: SelectOption<T>,
  value: T[],
  selectedOptions: SelectOption<T>[],
  maxSelections?: number
): { newValue: T[]; newOptions: SelectOption<T>[] } | null {
  if (option.disabled) { return null }
  const isSelected = value.includes(option.value)
  if (!isSelected && maxSelections && value.length >= maxSelections) { return null }
  const newValue = isSelected ? value.filter((v) => v !== option.value) : [...value, option.value]
  const newOptions = isSelected
    ? selectedOptions.filter((opt) => opt.value !== option.value)
    : [...selectedOptions, option]
  return { newValue, newOptions }
}

// ============================================================================
// Keyboard handlers
// ============================================================================

export function handleKeyWhenClosed(e: React.KeyboardEvent, setIsOpen: (v: boolean) => void): void {
  if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
    e.preventDefault()
    setIsOpen(true)
  }
}

interface OpenKeyHandlerArgs<T> {
  buttonRef: React.RefObject<HTMLButtonElement>
  e: React.KeyboardEvent
  filteredOptions: SelectOption<T>[]
  handleToggleOption: (opt: SelectOption<T>) => void
  highlightedIndex: number
  setHighlightedIndex: (fn: (prev: number) => number) => void
  setIsOpen: (v: boolean) => void
  setSearchQuery: (q: string) => void
}

export function handleKeyWhenOpen<T>(args: OpenKeyHandlerArgs<T>): void {
  const { buttonRef, e, filteredOptions, handleToggleOption, highlightedIndex, setHighlightedIndex, setIsOpen, setSearchQuery } = args
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    setHighlightedIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1))
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    setHighlightedIndex((prev) => Math.max(prev - 1, 0))
  } else if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    const option = filteredOptions[highlightedIndex]
    if (option) { handleToggleOption(option) }
  } else if (e.key === 'Escape') {
    e.preventDefault()
    setIsOpen(false)
    setSearchQuery('')
    buttonRef.current?.focus()
  }
}

// ============================================================================
// useMultiSelectHandlers — all event callbacks for MultiSelectInner
// ============================================================================

interface UseMultiSelectHandlersArgs<T> {
  filteredOptions: SelectOption<T>[]
  highlightedIndex: number
  isOpen: boolean
  maxSelections?: number
  onChange?: (values: T[], selectedOptions: SelectOption<T>[]) => void
  selectedOptions: SelectOption<T>[]
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>
  value: T[]
  buttonRef: React.RefObject<HTMLButtonElement>
}

interface MultiSelectHandlers<T> {
  handleClear: (e: React.MouseEvent | React.KeyboardEvent) => void
  handleClose: () => void
  handleKeyDown: (e: React.KeyboardEvent) => void
  handleRemoveChip: (optionValue: T) => void
  handleToggleOption: (option: SelectOption<T>) => void
}

export function useMultiSelectHandlers<T>({
  filteredOptions, highlightedIndex, isOpen, maxSelections, onChange,
  selectedOptions, setHighlightedIndex, setIsOpen, setSearchQuery, value, buttonRef,
}: UseMultiSelectHandlersArgs<T>): MultiSelectHandlers<T> {
  const handleClose = useCallback(
    () => { setIsOpen(false); setSearchQuery('') },
    [setIsOpen, setSearchQuery]
  )

  const handleToggleOption = useCallback(
    (option: SelectOption<T>) => {
      const result = computeToggleResult(option, value, selectedOptions, maxSelections)
      if (result) { onChange?.(result.newValue, result.newOptions) }
    },
    [value, selectedOptions, onChange, maxSelections]
  )

  const handleRemoveChip = useCallback(
    (optionValue: T) => {
      onChange?.(
        value.filter((v) => v !== optionValue),
        selectedOptions.filter((opt) => opt.value !== optionValue)
      )
    },
    [value, selectedOptions, onChange]
  )

  const handleClear = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => { e.stopPropagation(); onChange?.([], []) },
    [onChange]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) {
        handleKeyWhenClosed(e, setIsOpen)
      } else {
        handleKeyWhenOpen({
          buttonRef, e, filteredOptions, handleToggleOption,
          highlightedIndex, setHighlightedIndex, setIsOpen, setSearchQuery,
        })
      }
    },
    [isOpen, filteredOptions, highlightedIndex, handleToggleOption, setIsOpen, setHighlightedIndex, setSearchQuery, buttonRef]
  )

  return { handleClear, handleClose, handleKeyDown, handleRemoveChip, handleToggleOption }
}

// ============================================================================
// Dropdown open effects
// ============================================================================

export function useSearchFocusOnOpen(isOpen: boolean, searchInputRef: React.RefObject<HTMLInputElement>): void {
  useEffect(() => {
    /* v8 ignore next 3 -- DOM ref timing in useEffect */
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50)
    }
  }, [isOpen, searchInputRef])
}

export function useHighlightReset(
  filteredOptionsLength: number,
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>
): void {
  useEffect(() => {
    setHighlightedIndex(filteredOptionsLength > 0 ? 0 : -1)
  }, [filteredOptionsLength, setHighlightedIndex])
}

// ============================================================================
// Master setup hook — call from MultiSelectInner to avoid long function body
// ============================================================================

export interface MultiSelectRefs {
  buttonRef: React.RefObject<HTMLButtonElement>
  containerRef: React.RefObject<HTMLDivElement>
  dropdownRef: React.RefObject<HTMLDivElement>
  listRef: React.RefObject<HTMLDivElement>
  searchInputRef: React.RefObject<HTMLInputElement>
}

export interface MultiSelectResolvedProps<T> {
  className?: string
  error?: string
  helperText?: string
  id?: string
  isClearable: boolean
  isDisabled?: boolean
  isHideMessage: boolean
  isLoading: boolean
  isModal: boolean
  isShowCount: boolean
  maxSelections?: number
  maxVisibleItems: number
  noOptionsText: string
  placeholder: string
  renderChip?: MultiSelectProps<T>['renderChip']
  renderOption?: MultiSelectProps<T>['renderOption']
  searchPlaceholder: string
  size: 'sm' | 'md' | 'lg'
  value: T[]
}

export interface MultiSelectSetupResult<T> {
  dropdownPosition: { top: number; left: number; width: number } | null
  filteredOptions: SelectOption<T>[]
  handleClear: (e: React.MouseEvent | React.KeyboardEvent) => void
  handleClose: () => void
  handleKeyDown: (e: React.KeyboardEvent) => void
  handleRemoveChip: (v: T) => void
  handleToggleOption: (option: SelectOption<T>) => void
  highlightedIndex: number
  isMobile: boolean
  isOpen: boolean
  refs: MultiSelectRefs
  resolved: MultiSelectResolvedProps<T>
  searchQuery: string
  selectedOptions: SelectOption<T>[]
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>
  showError: boolean | '' | undefined
  validationState: SearchableSelectValidationState
}

export function useMultiSelectSetup<T>(
  props: MultiSelectProps<T>,
  isMobileHook: boolean,
  forwardedRef: React.ForwardedRef<HTMLButtonElement>
): MultiSelectSetupResult<T> {
  const {
    clearable: isClearable = true, className, disabled: isDisabled, error,
    filterOption = defaultFilterOption, helperText, hideMessage: isHideMessage = false,
    id, isLoading = false, maxSelections, maxVisibleItems = 8,
    modal: isModal = false, noOptionsText = 'No options found', onChange, onOpenChange,
    options, placeholder = 'Select...', renderChip, renderOption,
    searchPlaceholder = 'Search...', showCount: isShowCount = false,
    size = 'md', validationState: validationStateProp, value = [],
  } = props

  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const buttonRef = useRef<HTMLButtonElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  useImperativeHandle(forwardedRef, () => buttonRef.current as HTMLButtonElement, [])
  useEffect(() => { onOpenChange?.(isOpen) }, [isOpen, onOpenChange])

  const { selectedOptions, filteredOptions, validationState, showError } = useMultiSelectDerived({
    error, filterOption, options, searchQuery, validationStateProp, value,
  })
  const dropdownPosition = useDropdownPosition({
    containerRef, filteredOptionsCount: filteredOptions.length,
    isModal, isMobile: isMobileHook, isOpen, maxVisibleItems,
  })
  const { handleClear, handleClose, handleKeyDown, handleRemoveChip, handleToggleOption } =
    useMultiSelectHandlers({
      buttonRef, filteredOptions, highlightedIndex, isOpen, maxSelections,
      onChange, selectedOptions, setHighlightedIndex, setIsOpen, setSearchQuery, value,
    })

  useOutsideClick(isOpen, isMobileHook, containerRef, dropdownRef, handleClose); useBodyScrollLock(isOpen, isMobileHook)
  useSearchFocusOnOpen(isOpen, searchInputRef); useHighlightReset(filteredOptions.length, setHighlightedIndex)

  return {
    dropdownPosition, filteredOptions, handleClear, handleClose, handleKeyDown,
    handleRemoveChip, handleToggleOption, highlightedIndex, isMobile: isMobileHook,
    isOpen, refs: { buttonRef, containerRef, dropdownRef, listRef, searchInputRef },
    resolved: {
      className, error, helperText, id, isClearable, isDisabled, isHideMessage,
      isLoading, isModal, isShowCount, maxSelections, maxVisibleItems,
      noOptionsText, placeholder, renderChip, renderOption, searchPlaceholder, size, value,
    },
    searchQuery, selectedOptions, setIsOpen, setSearchQuery, showError, validationState,
  }
}

export { defaultFilterOption }
